// src/hooks/useAccountStatusMonitor.js
//
// Real-time monitor for the current user's account status.  Sets up an
// onSnapshot listener on the authenticated user's own clinician document
// and watches for three classes of mid-session change:
//
//   1. accountDisabled flipped to true  --> signals the overlay to block the UI
//   2. userType changed                 --> forces a token refresh so claims,
//                                           route guards and role helpers update
//   3. isAssignableClinician changed    --> same forced refresh
//
// This hook is the companion to the admin-side disable / change-type Cloud
// Functions.  It ensures that changes made by an administrator take effect
// on the target user's active session within seconds rather than waiting
// for the token to expire naturally (up to ~1 hour).
//
// Only active for Provider account types; Service Users (patients and
// informants) do not have clinician documents.

import { useEffect, useRef, useState, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAuth } from './useAuth';

export default function useAccountStatusMonitor() {
    const { user, claims, forceTokenRefresh } = useAuth();
    const [isAccountDisabled, setIsAccountDisabled] = useState(false);

    // Provider status as a STABLE primitive. useAuth's isProvider() is a brand
    // new function on every render, so if the listener effect depended on it
    // (or on any other useAuth callback) it would tear down and re-create the
    // Firestore subscription on every routine auth re-render. That churn is what
    // powered the logout error loop (an error -> forceTokenRefresh -> auth
    // re-render -> re-subscribe -> same error) and also cancelled the delayed
    // retry below. Depending on this string keeps the effect stable so it runs
    // once per genuine session change.
    const isProviderAccount = claims?.accountType === 'Provider';

    // Store previous field values so we can detect actual changes rather
    // than firing on the initial snapshot load.
    const prevValuesRef = useRef(null);
    const isInitialLoadRef = useRef(true);

    // Tracks scheduled (delayed) token-refresh timers so we can cancel them on
    // unmount / session change and avoid refreshing after we have stopped
    // monitoring. Because the effect deps are now stable, cleanup only runs on a
    // real session change, so these timers are no longer cancelled spuriously.
    const pendingRefreshTimersRef = useRef([]);

    // One-shot latch so a denied/flapping listener triggers at most ONE token
    // refresh per listener lifecycle. Reset every time we (re)subscribe below.
    // Belt-and-braces against the error loop even though stable deps already
    // prevent the re-subscribe cascade.
    const hasHandledSnapshotErrorRef = useRef(false);

    // Latest forceTokenRefresh kept in a ref so the callbacks and effect below
    // never need it as a dependency. forceTokenRefresh changes identity whenever
    // authState.user changes; depending on it directly is exactly what made the
    // listener re-subscribe after every refresh.
    const forceTokenRefreshRef = useRef(forceTokenRefresh);
    useEffect(() => {
        forceTokenRefreshRef.current = forceTokenRefresh;
    }, [forceTokenRefresh]);

    // Force a token refresh, then schedule a single delayed retry.
    //
    // Why the retry: the disable/change-type Cloud Functions write the Firestore
    // document FIRST (inside a transaction) and set the matching custom claims
    // immediately AFTER. Our onSnapshot listener fires on that document write, so
    // there is a narrow window where we could refresh the token before the new
    // claims have been written — leaving the session on stale (elevated) claims.
    // A short delayed retry reliably closes that race without hammering the token
    // endpoint (important for Microsoft SSO sessions, which dislike frequent
    // forced refreshes). Stable ([]) because it reads forceTokenRefresh via ref.
    const forceRefreshWithRetry = useCallback(() => {
        const attempt = (label) => forceTokenRefreshRef.current().catch((err) => {
            console.warn(`[useAccountStatusMonitor] Token refresh (${label}) failed:`, err);
        });
        attempt('immediate');
        const timer = setTimeout(() => attempt('retry'), 2500);
        pendingRefreshTimersRef.current.push(timer);
    }, []);

    // Stable callback that processes snapshot data.  Separated from the
    // effect so React can memoize it independently of the listener lifecycle.
    const handleSnapshot = useCallback((snapshot) => {
        if (!snapshot.exists()) {
            // No clinician document (e.g. deleted). There is nothing to disable
            // against, so clear any prior disabled state rather than leaving a
            // stale blocking overlay in place.
            setIsAccountDisabled(false);
            return;
        }

        const data = snapshot.data();
        const currentDisabled = data.accountDisabled === true;
        const currentUserType = data.userType;
        const currentAssignable = data.isAssignableClinician;

        // Always reflect the disabled state immediately (including on
        // initial load -- if the user managed to reach the app while
        // disabled, the overlay should appear at once).
        setIsAccountDisabled(currentDisabled);

        const prev = prevValuesRef.current;

        if (isInitialLoadRef.current) {
            // Capture the baseline on first load; do not trigger a refresh.
            isInitialLoadRef.current = false;
            prevValuesRef.current = {
                userType: currentUserType,
                isAssignableClinician: currentAssignable,
            };
            return;
        }

        if (!prev) return;

        // Detect mid-session changes to role fields and force a token
        // refresh so that useAuth picks up the new custom claims.
        const typeChanged = currentUserType !== prev.userType;
        const assignableChanged = currentAssignable !== prev.isAssignableClinician;

        if (typeChanged || assignableChanged) {
            forceRefreshWithRetry();
        }

        // Update the ref for the next comparison.
        prevValuesRef.current = {
            userType: currentUserType,
            isAssignableClinician: currentAssignable,
        };
    }, [forceRefreshWithRetry]);

    useEffect(() => {
        // Only monitor Provider accounts -- Service Users do not have
        // clinician documents. When we are not monitoring (signed out, or a
        // non-provider), clear the disabled flag so the full-screen overlay can
        // never linger over the login page after a disabled user signs out.
        if (!user?.uid || !isProviderAccount) {
            setIsAccountDisabled(false);
            return;
        }

        // Reset state for this user session.
        isInitialLoadRef.current = true;
        prevValuesRef.current = null;
        hasHandledSnapshotErrorRef.current = false;

        const clinicianRef = doc(db, 'clinicians', user.uid);
        const unsubscribe = onSnapshot(
            clinicianRef,
            handleSnapshot,
            (error) => {
                // A permission-denied here is EXPECTED and harmless during a
                // normal sign-out: the instant the user signs out,
                // auth.currentUser becomes null and this listener loses read
                // access to its own clinician doc. Bail immediately in that case
                // — there is no session to refresh, and calling forceTokenRefresh
                // would restart the "Snapshot error -> Forcing token refresh"
                // loop the tester saw on logout.
                if (!auth.currentUser) return;

                // De-dupe: react to only the first error for this listener
                // instance (see hasHandledSnapshotErrorRef above).
                if (hasHandledSnapshotErrorRef.current) return;
                hasHandledSnapshotErrorRef.current = true;

                // Genuine "session invalidated server-side" case (do NOT fail
                // open): the account was disabled/deleted or its claims were
                // stripped and the token can no longer be refreshed. Force a
                // refresh so useAuth's account-invalidation handler signs the
                // user out, closing the window where a disabled user keeps read
                // access until their token naturally expires (up to ~1 hour).
                console.error('[useAccountStatusMonitor] Snapshot error:', error);
                forceTokenRefreshRef.current().catch((refreshErr) => {
                    console.warn(
                        '[useAccountStatusMonitor] Token refresh after snapshot error failed:',
                        refreshErr,
                    );
                });
            },
        );

        return () => {
            unsubscribe();
            // Cancel any pending delayed token-refresh retries for this session.
            pendingRefreshTimersRef.current.forEach(clearTimeout);
            pendingRefreshTimersRef.current = [];
            // Drop the disabled flag when we stop monitoring this session so a
            // stale overlay cannot block the next screen (e.g. login).
            setIsAccountDisabled(false);
        };
    }, [user?.uid, isProviderAccount, handleSnapshot]);

    return { isAccountDisabled };
}
